import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import ParticipantHistory from "../component/learn_earn/ParticipantHistory";
import PointHistory from "../component/learn_earn/PointHistory";
import RedeemPointsPopover from "../component/learn_earn/RedeemPointsPopover";
import JoinLearnAndEarn from "../component/learn_earn/JoinLearnAndEarn";
import QuizModal from "../component/learn_earn/QuizModal";
import MessageToster from "../component/MessageToster";
import toast from "react-hot-toast";
import { formatDateDDMMYYYY, getApiHeaders } from "../utils/helpers";
import { useUser } from "../context/UserContext";
import { BASE_URL } from "../api";

export default function LearnAndEarn() {
  const navigate = useNavigate();
  const { userData: contextUserData } = useUser() || {};
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [learnEarnStatus, setLearnEarnStatus] = useState(null);
  const [localUserData, setLocalUserData] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [participantStatus, setParticipantStatus] = useState(null);
  const activeUserData = localUserData || contextUserData;
  const pointsPerUsd =
    activeUserData?.points_per_usd != null ? Number(activeUserData.points_per_usd) : 500;
  const minRedeemPoints =
    activeUserData?.min_redeem_points != null ? Number(activeUserData.min_redeem_points) : 20000;

  const [userStats, setUserStats] = useState({
    joiningDate: "-",
    totalPoints: 0,
    usdEstimate: "0.00",
    currentLevel: "Basic",
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const [hasQuizUpdated, setHasQuizUpdated] = useState(false);

  const fetchUserDataStatus = useCallback(async (showFullLoader = false) => {
    if (showFullLoader) {
      setLoadingStatus(true);
    }
    try {
      const res = await axios.get(`${BASE_URL}/account/get_user_data`, {
        headers: getApiHeaders(),
      });

      if (res.data?.code === 200 && res.data?.data) {
        const data = res.data.data;
        setLocalUserData(data);
        setLearnEarnStatus(data?.learn_earn_status ?? "Inactive");
      } else {
        setLearnEarnStatus("Inactive");
      }
    } catch (err) {
      console.error("Error fetching get_user_data in LearnAndEarn:", err);
      setLearnEarnStatus("Inactive");
    } finally {
      if (showFullLoader) {
        setLoadingStatus(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchUserDataStatus(true);
  }, [fetchUserDataStatus]);

  const handleJoinProgram = async () => {
    setIsJoining(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/account/learn_earn_enroll`,
        {},
        { headers: getApiHeaders() }
      );

      if (
        (res?.data?.code === 200 || res?.data?.status === "success" || res?.data?.status === true) &&
        res?.data?.code !== 400
      ) {
        MessageToster(
          "success",
          res.data?.message || res.data?.msg || "Successfully enrolled in Learn & Earn!"
        );
        await fetchUserDataStatus(false);
      } else {
        MessageToster(
          "error",
          res?.data?.message || res?.data?.msg || "Failed to join program"
        );
      }
    } catch (err) {
      console.error("Error enrolling in Learn & Earn:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Something went wrong while enrolling in the program.";
      MessageToster("error", errMsg);
    } finally {
      setIsJoining(false);
    }
  };

  const handleOpenDetails = (item) => {
    navigate("/point-details", { state: { pointData: item } });
  };

  const handleStatusLoaded = useCallback(
    (statusData) => {
      if (!statusData) return;
      setParticipantStatus(statusData);
      setUserStats((prev) => ({
        ...prev,
        joiningDate: formatDateDDMMYYYY(statusData.joining_date) || prev.joiningDate,
        totalPoints: statusData.total_points ?? prev.totalPoints,
        usdEstimate:
          statusData.total_points_usd != null
            ? Number(statusData.total_points_usd).toFixed(2)
            : ((statusData.total_points ?? 0) / pointsPerUsd).toFixed(2),
        currentLevel: statusData.current_level_name || statusData.level_name || prev.currentLevel,
        currentLevelNo: statusData.current_level_no ?? statusData.level_no ?? 1,
      }));
    },
    [pointsPerUsd]
  );

  const handlePointsLoaded = useCallback(
    (list) => {
      if (!list || list.length === 0) return;
      setUserStats((prev) => {
        if (prev.joiningDate !== "-") return prev;
        const totalPts = list.reduce((sum, item) => {
          const p = Number(item.points ?? item.point ?? 0) || 0;
          return String(item.type || "").toLowerCase() === "debit" ? sum - p : sum + p;
        }, 0);
        const latest = list[0];
        return {
          ...prev,
          totalPoints: totalPts,
          usdEstimate: (totalPts / pointsPerUsd).toFixed(2),
          currentLevel: latest?.level_name || latest?.levelName || prev.currentLevel,
        };
      });
    },
    [pointsPerUsd]
  );

  const handleRedeemSuccess = (redeemedAmount) => {
    setUserStats((prev) => {
      const newTotal = Math.max(0, prev.totalPoints - redeemedAmount);
      return {
        ...prev,
        totalPoints: newTotal,
        usdEstimate: (newTotal / pointsPerUsd).toFixed(2),
      };
    });
    setRefreshKey((prev) => prev + 1);
  };

  const handleGoToExam = () => {
    setHasQuizUpdated(false);
    setIsQuizModalOpen(true);
  };

  const handleAnswerSubmitted = () => {
    // Record that answers were submitted, but do not reload background page or unmount tables while quiz is open
    setHasQuizUpdated(true);
  };

  const handleQuizSuccess = (earnedPoints) => {
    setHasQuizUpdated(true);
  };

  const handleCloseQuizModal = () => {
    setIsQuizModalOpen(false);
    if (hasQuizUpdated) {
      setRefreshKey((prev) => prev + 1);
      fetchUserDataStatus(false);
      setHasQuizUpdated(false);
    }
  };

  const isActive = String(learnEarnStatus || "").toLowerCase() === "active";

  return (
    <div id="layout-wrapper">
      <Topbar />
      <Sidebar />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid mb-4">
            {loadingStatus ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ minHeight: "55vh" }}
              >
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "2.5rem", height: "2.5rem" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="text-muted mt-3 font-size-13 fw-medium">
                  Checking Learn &amp; Earn status...
                </div>
              </div>
            ) : isActive ? (
              <>
                <div className="row">
                  <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                      <div>
                        <h2 className="fw-700">Learn &amp; Earn Participants</h2>
                        <p className="text-muted mb-sm-1 font-size-13">
                          Complete learning tasks and unlock rewards
                        </p>
                      </div>

                      <div className="page-title-right d-flex align-items-center gap-2">
                        <div className="position-relative">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                            onClick={() => setIsRedeemOpen((prev) => !prev)}
                          >
                            <i className="bx bx-gift me-2 fs-5"></i> Redeem Points
                          </button>

                          <RedeemPointsPopover
                            isOpen={isRedeemOpen}
                            onClose={() => setIsRedeemOpen(false)}
                            totalPoints={userStats.totalPoints}
                            conversionRate={pointsPerUsd}
                            minRequired={minRedeemPoints}
                            onSuccess={handleRedeemSuccess}
                          />
                        </div>

                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                          onClick={handleGoToExam}
                        >
                          <i className="bx bx-book-open me-2 fs-5"></i> Start Quiz
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-4">
                    <div className="card card-animate overflow-hidden bg-primary-subtle h-100 mb-0 shadow-sm border-0">
                      <div className="card-body upcoming-box p-4">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="me-3">
                            <p className="text-muted mb-2 fw-medium text-uppercase font-size-12">
                              Joining Date
                            </p>
                            <h5 className="mb-0 fw-bold font-size-15 text-dark text-break">
                              {userStats.joiningDate}
                            </h5>
                          </div>
                          <div className="mini-stat-icon avatar-sm flex-shrink-0 ms-auto">
                            <div className="avatar-title bg-primary rounded-circle upcoming-icon text-white font-size-22">
                              <i className="bx bx-calendar" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="card card-animate overflow-hidden bg-success-subtle h-100 mb-0 shadow-sm border-0">
                      <div className="card-body upcoming-box p-4">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="me-3">
                            <p className="text-muted mb-2 fw-medium text-uppercase font-size-12">
                              Total Points
                            </p>
                            <h5 className="mb-0 fw-bold font-size-18 text-dark d-flex align-items-baseline gap-2">
                              <span>{userStats.totalPoints}</span>
                              <span className="font-size-12 text-muted fw-normal">
                                ~ {userStats.usdEstimate} USD
                              </span>
                            </h5>
                          </div>
                          <div className="mini-stat-icon avatar-sm flex-shrink-0 ms-auto">
                            <div className="avatar-title bg-success rounded-circle upcoming-icon text-white font-size-22">
                              <i className="bx bxs-star" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="card card-animate overflow-hidden bg-warning-subtle h-100 mb-0 shadow-sm border-0">
                      <div className="card-body upcoming-box p-4">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="me-3">
                            <p className="text-muted mb-2 fw-medium text-uppercase font-size-12">
                              Current Level
                            </p>
                            <h5 className="mb-0 fw-bold font-size-18 text-dark">
                              {userStats.currentLevel}
                            </h5>
                          </div>
                          <div className="mini-stat-icon avatar-sm flex-shrink-0 ms-auto">
                            <div className="avatar-title bg-warning rounded-circle upcoming-icon text-white font-size-22">
                              <i className="bx bx-check-circle" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-12 col-lg-5">
                    <ParticipantHistory
                      key={`participant-${refreshKey}`}
                      onStatusLoaded={handleStatusLoaded}
                    />
                  </div>

                  <div className="col-12 col-lg-7">
                    <PointHistory
                      key={`point-${refreshKey}`}
                      onViewDetails={handleOpenDetails}
                      onDataLoaded={handlePointsLoaded}
                    />
                  </div>
                </div>
              </>
            ) : (
              <JoinLearnAndEarn
                pointsPerUsd={pointsPerUsd}
                minRedeemPoints={minRedeemPoints}
                onJoin={handleJoinProgram}
                isJoining={isJoining}
              />
            )}

            <QuizModal
              isOpen={isQuizModalOpen}
              onClose={handleCloseQuizModal}
              levelNo={
                participantStatus?.current_level_no ??
                participantStatus?.level_no ??
                userStats?.currentLevelNo ??
                activeUserData?.level_no ??
                1
              }
              levelName={
                participantStatus?.current_level_name ??
                participantStatus?.level_name ??
                userStats?.currentLevel ??
                activeUserData?.level_name ??
                "Basic"
              }
              points={
                participantStatus?.points_per_quiz ??
                participantStatus?.point ??
                50
              }
              onAnswerSubmitted={handleAnswerSubmitted}
              onSuccess={handleQuizSuccess}
            />
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
